import { useState } from 'react';
import { useI18n } from '@/i18n';
import { dataService } from '@/services/dataService';
import { useToast } from '@/components/ui/Toast';

type Method = 'percentage' | 'fixed' | 'commission' | 'hybrid' | 'per_project';

/**
 * A real, working local calculator — not a fake widget. It computes and
 * can save the result against the partnership's ledger, but it is
 * explicitly labeled PREVIEW CALCULATION and is not accounting/legal
 * advice, per the brief.
 */
export function ProfitSharingCalculator({ partnershipId, onSaved }: { partnershipId: string; onSaved: () => void }) {
  const { t } = useI18n();
  const push = useToast();
  const [method, setMethod] = useState<Method>('percentage');
  const [revenue, setRevenue] = useState(0);
  const [eligibleRevenue, setEligibleRevenue] = useState(0);
  const [partnerPct, setPartnerPct] = useState(30);
  const [fixedAmount, setFixedAmount] = useState(0);
  const [adjustments, setAdjustments] = useState(0);
  const [periodLabel, setPeriodLabel] = useState('2026-08');

  const base = eligibleRevenue || revenue;
  const partnerShare = method === 'fixed' ? fixedAmount : Math.round((base * partnerPct) / 100);
  const ugaritsShare = Math.max(0, base - partnerShare + adjustments);
  const balance = partnerShare - 0;

  async function save() {
    await dataService.addPartnershipLedgerEntry(partnershipId, {
      periodLabel, grossRevenue: revenue, eligibleRevenue: base, partnerShare, ugaritsShare, amountPaid: 0, balance,
    });
    push(t('toast.calculationSaved'), 'success');
    onSaved();
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="soon-tag-inline" style={{ marginBottom: 10, display: 'inline-block' }}>{t('profitSharing.previewCalculation')}</div>
      <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 0 }}>{t('profitSharing.disclaimer')}</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={{ fontSize: 12 }}>{t('profitSharing.method')}
          <select className="text-input" value={method} onChange={(e) => setMethod(e.target.value as Method)}>
            {(['percentage', 'fixed', 'commission', 'hybrid', 'per_project'] as Method[]).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>{t('profitSharing.period')}
          <input className="text-input" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} />
        </label>
        <label style={{ fontSize: 12 }}>{t('profitSharing.revenue')}
          <input type="number" className="text-input" value={revenue} onChange={(e) => setRevenue(Number(e.target.value))} />
        </label>
        <label style={{ fontSize: 12 }}>{t('profitSharing.eligibleRevenue')}
          <input type="number" className="text-input" value={eligibleRevenue} onChange={(e) => setEligibleRevenue(Number(e.target.value))} />
        </label>
        {method !== 'fixed' ? (
          <label style={{ fontSize: 12 }}>{t('profitSharing.partnerPct')}
            <input type="number" className="text-input" value={partnerPct} onChange={(e) => setPartnerPct(Number(e.target.value))} />
          </label>
        ) : (
          <label style={{ fontSize: 12 }}>{t('profitSharing.fixedAmount')}
            <input type="number" className="text-input" value={fixedAmount} onChange={(e) => setFixedAmount(Number(e.target.value))} />
          </label>
        )}
        <label style={{ fontSize: 12 }}>{t('profitSharing.adjustments')}
          <input type="number" className="text-input" value={adjustments} onChange={(e) => setAdjustments(Number(e.target.value))} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 24, marginTop: 16, fontSize: 13 }}>
        <div><div style={{ color: 'var(--text-3)' }}>{t('profitSharing.partnerShare')}</div><div style={{ fontWeight: 700 }}>{partnerShare.toLocaleString()}</div></div>
        <div><div style={{ color: 'var(--text-3)' }}>{t('profitSharing.ugaritsShare')}</div><div style={{ fontWeight: 700 }}>{ugaritsShare.toLocaleString()}</div></div>
        <div><div style={{ color: 'var(--text-3)' }}>{t('profitSharing.balance')}</div><div style={{ fontWeight: 700 }}>{balance.toLocaleString()}</div></div>
      </div>

      <button className="btn-primary" style={{ marginTop: 14 }} onClick={save}>{t('profitSharing.saveDemo')}</button>
    </div>
  );
}
